import { ValidationErrors } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppDialogService } from '@services/app-dialog.service';
import {
  AuthService,
  ILoginPageModel,
  IResetPasswordResponse,
} from '@services/auth.service';
import { finalize, map, take } from 'rxjs';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit {
  @ViewChild('forgotPasswordForm') forgotPasswordForm: any;

  logoHyperlink: string;
  desktopLogoUrl: string;
  appleStoreLink: string;
  googleStoreLink: string;
  userNameError: string;
  passwordError: string;

  forgotPwdFormError: string;
  forgotPasswordDialogRef: any;
  forgotPwdUserNameError: string;
  forgotPwdEmailError: string;
  isLoggingIn: boolean = false;
  sessionExpired: boolean = false;
  /** The forgot-password dialog was opened by an expired password, not by the link. */
  passwordExpired: boolean = false;
  isResettingPassword: boolean = false;

  constructor(
    private _as: AuthService,
    private _router: Router,
    private _cdr: ChangeDetectorRef,
    private _ads: AppDialogService,
    private _ar: ActivatedRoute
  ) {
    this.desktopLogoUrl = window['loginLogoUrl'];
  }

  ngOnInit() {
    // AuthInterceptor redirects here with reason=expired when the server 401s a live session,
    // so say why they are looking at a login form instead of the page they were on.
    this.sessionExpired =
      this._ar.snapshot.queryParamMap.get('reason') === 'expired';

    // The redirect for an already-authenticated visitor used to live here, commented out. It could
    // not have worked: loggedIn$ is a plain Subject, so it never replays for a session that was
    // already authenticated when this page loaded and the take(1) simply never fires, and
    // redirectUserToDashboard() no longer exists. AnonymousOnlyGuard does the job on the route,
    // before this component is ever constructed.
  }

  onForgotPasswordClick($event: any) {
    this.forgotPasswordDialogRef = this._ads.showDialogReturnRef(
      this.forgotPasswordForm,
    );
    this.forgotPasswordDialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      this.passwordExpired = false;
      this.clearForgotPasswordForm();
    });
  }

  onBackClick($event: any) {
    this.forgotPasswordDialogRef.close();
  }

  onLoginClick(event: { userName: string; password: string }) {
    this.logUserIn(event.userName, event.password);
  }

  onResetPasswordClick(event: { userName: string; email: string }) {
    this.resetUserPassword(event.userName, event.email);
  }

  clearExistingFormErrors() {
    this.userNameError = '';
    this.passwordError = '';
    this.forgotPwdUserNameError = '';
    this.forgotPwdEmailError = '';
    this.forgotPwdFormError = '';
    this._cdr.markForCheck();
  }

  clearForgotPasswordForm() {
    // Clear error messages
    this.clearExistingFormErrors();
    
    // Clear form input values if they exist
    const forgotPwdUserNameInput = document.getElementById('forgotPwdUserName') as HTMLInputElement;
    const forgotPwdEmailInput = document.getElementById('forgotPwdEmail') as HTMLInputElement;
    
    if (forgotPwdUserNameInput) {
      forgotPwdUserNameInput.value = '';
    }
    if (forgotPwdEmailInput) {
      forgotPwdEmailInput.value = '';
    }
    
    this._cdr.markForCheck();
  }

  private logUserIn(userName: string, password: string) {
    // Once they submit, any login error replaces the notice rather than stacking under it.
    this.sessionExpired = false;
    this.isLoggingIn = true;
    this._cdr.markForCheck();
    this._as
      .login(userName, password)
      .pipe(
      )
      .subscribe({
        next: (response) => {
          if (response?.loginData?.error) {
            this.processLoginErrorResponse(response);
            return;
          }
          this.processLoginSuccessResponse(response);
        },
        error: (error: HttpErrorResponse) => {
          this.processLoginErrorResponse(error);
          console.log(error);
        },
      });
  }

  private processLoginErrorResponse(
    response: ILoginPageModel | HttpErrorResponse,
  ) {
    this.isLoggingIn = false;
    this._cdr.markForCheck();
    this._ads.showErrorMessage('Login Failed');

    if (response instanceof HttpErrorResponse) {
      const httpResponse = response as HttpErrorResponse;
      console.log(httpResponse);
      const body = JSON.parse(httpResponse.error);

      // The password was right but has expired, and the org wants it reset by email rather than
      // changed after signing in - so go straight to the form that does that.
      if (body?.passwordResetRequired) {
        this.passwordExpired = true;
        this.onForgotPasswordClick(null);
        return;
      }

      const errors = body?.errors as ValidationErrors;
      this.userNameError = errors?.username?.[0];
      this.passwordError = errors?.password?.[0];

      this._cdr.markForCheck();
      return;
    }

    console.log(response.loginData.error);
    this.userNameError = response.loginData.error;
    this._cdr.markForCheck();
  }

  private processPasswordResetErrorResponse(
    response: IResetPasswordResponse | HttpErrorResponse,
  ) {
    this._ads.showErrorMessage('Password reset failed');
    console.log(response);

    if (response instanceof HttpErrorResponse) {
      const errors = JSON.parse(response.error)?.errors as ValidationErrors;
      this.forgotPwdEmailError = errors?.email?.[0];
      this.forgotPwdUserNameError = errors?.userName?.[0];
      this._cdr.markForCheck();
      return;
    }

    this.forgotPwdFormError = (
      response?.errorMessages ?? ([] as string[])
    ).join('\n');
    this._cdr.markForCheck();
  }

  private processLoginSuccessResponse(response: ILoginPageModel) {
    console.log(response);
    this._as.loggedIn$.next(true);
    this._as
      .getUserInformation(true)
      .pipe(take(1))
      .subscribe({
        next: userInfo => {
          if (userInfo && userInfo.mustChangePassword) {
            return;
          }

          const preferredHomepage = userInfo?.userPreferences?.homepage;
          const homepage =
            preferredHomepage === 'dashboard' || preferredHomepage === 'tank'
              ? preferredHomepage
              : 'dashboard';

          this._router.navigate([homepage]);
        },
        error: () => {
          this._router.navigate(['dashboard']);
        },
      });
  }

  private resetUserPassword(userName: string, email: string) {
    this.isResettingPassword = true;
    this.forgotPwdUserNameError = '';
    this.forgotPwdEmailError = '';
    this._cdr.markForCheck();

    this._as
      .resetUserPassword(userName, email)
      .pipe(
        finalize(() => {
          this.isResettingPassword = false;
          this._cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response: IResetPasswordResponse) => {
          if (!response?.success) {
            this.processPasswordResetErrorResponse(response);
            return;
          }
          this._ads.showSuccessMessage('Password reset email sent');
          this.forgotPasswordDialogRef.close();
        },
        error: (error: HttpErrorResponse) => {
          this.processPasswordResetErrorResponse(error);
          console.log(error);
        },
      });
  }
}
